import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios'
import { config } from '@/config'

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

function transformKeys(data: unknown, transformer: (key: string) => string): unknown {
  if (Array.isArray(data)) {
    return data.map((item) => transformKeys(item, transformer))
  }
  if (data !== null && typeof data === 'object' && !(data instanceof File) && !(data instanceof Blob) && !(data instanceof FormData)) {
    return Object.fromEntries(
      Object.entries(data as Record<string, unknown>).map(([key, value]) => [
        transformer(key),
        transformKeys(value, transformer),
      ])
    )
  }
  return data
}

let accessToken: string | null = null
let refreshPromise: Promise<string | null> | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

function createBaseClient(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  instance.interceptors.request.use((req: InternalAxiosRequestConfig) => {
    if (accessToken && req.headers) {
      req.headers.Authorization = `Bearer ${accessToken}`
    }
    if (req.data && !(req.data instanceof FormData)) {
      req.data = transformKeys(req.data, camelToSnake)
    }
    if (req.params) {
      req.params = transformKeys(req.params, camelToSnake)
    }
    return req
  })

  instance.interceptors.response.use(
    (res: AxiosResponse) => {
      if (res.data && typeof res.data === 'object') {
        res.data = transformKeys(res.data, snakeToCamel)
      }
      return res
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
      const isRefreshRequest = originalRequest.url?.includes('/auth/refresh')

      if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
        originalRequest._retry = true

        const newToken = await refreshAccessToken()
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return instance(originalRequest)
        }
      }

      return Promise.reject(error)
    }
  )

  return instance
}

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise

  refreshPromise = authClient
    .post('/auth/refresh')
    .then((res) => {
      const token =
        res.data?.data?.tokens?.accessToken ??
        res.data?.data?.accessToken ??
        res.data?.accessToken ??
        null
      if (token) {
        setAccessToken(token)
      }
      return token
    })
    .catch(() => {
      setAccessToken(null)
      return null
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

export const apiClient: AxiosInstance = createBaseClient(config.apiUrl)

export const authClient: AxiosInstance = createBaseClient(config.authUrl)
